// Shared types + display helpers for the Owner Statements panel. Mirrors the
// shapes returned by the /backend/statements/* functions.

export type OwnerVehicleRow = {
  owner_id: string
  owner_name: string
  owner_email: string
  owner_status: string
  vehicle_id: string
  plate: string
  vehicle_name: string
  lifecycle_status: string
  operational_state: string
  operational_state_as_of: string | null
  commercial_model: string
  statement_count: number
  first_statement_period: string | null
  latest_statement_period: string | null
  latest_retention_balance_zar: number
  latest_closing_balance_zar: number
  any_source_errors: boolean
  has_draft_statement: boolean
}

// An owner rolled up from their vehicle rows, for the master list.
export type OwnerGroup = {
  owner_id: string
  owner_name: string
  owner_email: string
  owner_status: string
  vehicles: OwnerVehicleRow[]
  vehicle_count: number
  statement_total: number
  any_source_errors: boolean
  has_draft_statement: boolean
}

export type StatementPeriod = {
  statement_id: string
  owner_id: string
  vehicle_id: string
  plate: string
  period_start: string
  period_end: string
  period_label: string
  operational_state: string
  source: string
  source_ref: string
  generated_at: string | null
  has_source_errors: boolean
  statement_pdf_path: string | null
  gross_earnings_zar: number
  total_earnings_zar: number
  total_cost_of_sales_zar: number
  gross_profit_zar: number
  total_operational_costs_zar: number
  net_zar: number
  opening_balance_zar: number
  closing_balance_zar: number
  monthly_retention_zar: number
  retention_balance_zar: number
  payments_zar: number
  shifts_count: number
  days_count: number
  kilometres: number
  earnings_per_shift_zar: number
  earnings_per_km_zar: number
  net_per_km_zar: number
  drivers_count: number
  shifts_in_operational_data: number
  is_draft: boolean
}

export type StatementLine = {
  statement_id: string
  vehicle_id: string
  period_start: string
  period_label: string
  line_code: string
  label: string
  description: string | null
  section: string
  sort_order: number
  is_subtotal: boolean
  is_performance: boolean
  amount_zar: number | null
  raw_value: string | null
  is_error: boolean
  error_note: string | null
}

export type VehiclePerformanceRow = {
  vehicle_id: string
  plate: string
  vehicle_name: string
  owner_id: string
  period_start: string
  period_label: string
  operational_state: string
  earnings_zar: number | null
  net_zar: number | null
  shifts_count: number
  days_count: number
  kilometres: number | null
  earnings_per_shift_zar: number | null
  earnings_per_km_zar: number | null
  net_per_km_zar: number | null
  service_cost_zar: number | null
  maintenance_cost_zar: number | null
  minor_repairs_zar: number | null
  carwashes_zar: number | null
  petrol_zar: number | null
  insurance_zar: number | null
  drivers_count: number
  has_source_errors: boolean
}

export type FleetStateCount = { operational_state: string; count: number }

export type ReconciliationRow = {
  plate: string
  owner_name: string
  lifecycle_status: string
  sheet_operational_state: string
  operational_state_as_of: string | null
  discrepancy: string
}

export type StateResolutionRow = {
  event_id: string
  plate: string
  winner: string
  discrepancy: string
  resolved_by_email: string
  resolved_by_name: string
  resolved_at: string | null
  before_lifecycle_status: string
  before_operational_state: string
  after_lifecycle_status: string
  after_operational_state: string
  undone: boolean
}

export type FleetStateSummary = {
  counts: FleetStateCount[]
  reconciliation: ReconciliationRow[]
  resolutions: StateResolutionRow[]
}

// -- Money -----------------------------------------------------------------

const zarFmt = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

// South African rand with a thousands separator and 2 decimals. Negatives are
// wrapped in parentheses (accounting style); callers colour negatives red.
export function formatZar(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  const abs = Math.abs(value)
  const body = `R${zarFmt.format(abs)}`
  return value < 0 ? `(${body})` : body
}

const zarCompactFmt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })

// Compact rand for chart axes, e.g. "R1.2m", "R340k".
export function formatZarCompact(value: number): string {
  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''
  if (abs >= 1_000_000) return `${sign}R${(abs / 1_000_000).toFixed(1)}m`
  if (abs >= 1_000) return `${sign}R${Math.round(abs / 1_000)}k`
  return `${sign}R${zarCompactFmt.format(abs)}`
}

const numFmt = new Intl.NumberFormat('en-US')

export function formatNumber(value: number, decimals = 0): string {
  if (!Number.isFinite(value)) return '—'
  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

export function formatInt(value: number): string {
  return numFmt.format(Math.round(value))
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' })
}

// -- Operational state ------------------------------------------------------

export type OperationalStateConfig = {
  label: string
  // Chip classes (light + dark) and a solid dot colour for compact contexts.
  chip: string
  dot: string
}

// Colours are fixed by the spec and do NOT auto-adapt, so each carries an
// explicit dark: variant for legibility in both modes.
const OPERATIONAL_STATE: Record<string, OperationalStateConfig> = {
  fully_active: {
    label: 'Fully active',
    chip: 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-200 dark:border-emerald-800',
    dot: 'bg-emerald-500',
  },
  partially_active_repairs: {
    label: 'Partially active (repairs)',
    chip: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950 dark:text-amber-200 dark:border-amber-800',
    dot: 'bg-amber-500',
  },
  repairs_licensing: {
    label: 'Repairs / licensing',
    chip: 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-950 dark:text-orange-200 dark:border-orange-800',
    dot: 'bg-orange-500',
  },
  new_model: {
    label: 'New model',
    chip: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950 dark:text-blue-200 dark:border-blue-800',
    dot: 'bg-blue-500',
  },
  offboarding: {
    label: 'Offboarding',
    chip: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700',
    dot: 'bg-gray-400',
  },
  offboarded: {
    label: 'Offboarded',
    chip: 'bg-gray-200 text-gray-800 border-gray-300 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-700',
    dot: 'bg-gray-600',
  },
}

export function humanize(code: string | null | undefined): string {
  if (!code) return 'Unknown'
  return code
    .split('_')
    .map((w) => (w.length ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(' ')
}

export function operationalStateConfig(code: string | null | undefined): OperationalStateConfig {
  if (code && OPERATIONAL_STATE[code]) return OPERATIONAL_STATE[code]!
  return {
    label: humanize(code),
    chip: 'bg-muted text-muted-foreground border-border',
    dot: 'bg-muted-foreground',
  }
}

// Ordered list of operational states for the fleet strip (known first, in a
// meaningful lifecycle order); unknown states are appended by the caller.
export const OPERATIONAL_STATE_ORDER: string[] = [
  'fully_active',
  'partially_active_repairs',
  'repairs_licensing',
  'new_model',
  'offboarding',
  'offboarded',
]

// -- Statement sections -----------------------------------------------------

export type SectionConfig = { code: string; label: string }

// Fixed render order for statement sections.
export const STATEMENT_SECTIONS: SectionConfig[] = [
  { code: 'balance', label: 'Balance' },
  { code: 'earnings', label: 'Earnings' },
  { code: 'cost_of_sales', label: 'Cost of sales' },
  { code: 'summary', label: 'Summary' },
  { code: 'operational_costs', label: 'Operational costs' },
  { code: 'retention', label: 'Retention' },
  { code: 'payments', label: 'Payments' },
]

const SECTION_RANK: Record<string, number> = Object.fromEntries(
  STATEMENT_SECTIONS.map((s, i) => [s.code, i]),
)

export function sectionRank(code: string): number {
  return SECTION_RANK[code] ?? STATEMENT_SECTIONS.length
}

export function sectionLabel(code: string): string {
  const found = STATEMENT_SECTIONS.find((s) => s.code === code)
  return found ? found.label : humanize(code)
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '—'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}
